import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const AdminPlanesCuotas = () => {
  const { API } = useContext(AuthContext);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todos');
  const [verRecibo, setVerRecibo] = useState(null);

  const getToken = () => localStorage.getItem('token') || localStorage.getItem('access_token');
  // Build base URL for uploaded files (remove /api suffix)
  const BASE_URL = API.replace('/api', '');

  useEffect(() => { loadPlanes(); }, []);

  const loadPlanes = async () => {
    try {
      const res = await axios.get(`${API}/admin/planes-cuotas`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPlanes(res.data.planes || []);
    } catch (err) {
      toast.error('Error cargando planes de cuotas');
    } finally {
      setLoading(false);
    }
  };

  const aprobarCuota = async (planId, num) => {
    try {
      await axios.put(`${API}/admin/planes-cuotas/${planId}/aprobar-cuota/${num}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Cuota ${num} aprobada`);
      loadPlanes();
    } catch (err) {
      toast.error('Error al aprobar cuota');
    }
  };

  const rechazarCuota = async (planId, num) => {
    const motivo = prompt('Motivo del rechazo:');
    if (!motivo) return;
    try {
      await axios.put(`${API}/admin/planes-cuotas/${planId}/rechazar-cuota/${num}`, { motivo }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Cuota ${num} rechazada`);
      loadPlanes();
    } catch (err) {
      toast.error('Error al rechazar');
    }
  };

  const extenderPlazo = async (planId, num) => {
    const dias = prompt('Dias a extender:', '7');
    if (!dias) return;
    try {
      await axios.put(`${API}/admin/planes-cuotas/${planId}/extender-plazo/${num}`, { dias: parseInt(dias) }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Plazo extendido ${dias} dias`);
      loadPlanes();
    } catch (err) {
      toast.error('Error al extender plazo');
    }
  };

  const marcarPagada = async (planId, num) => {
    if (!window.confirm(`Marcar cuota ${num} como pagada manualmente?`)) return;
    try {
      await axios.put(`${API}/admin/planes-cuotas/${planId}/marcar-pagada/${num}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`Cuota ${num} marcada como pagada`);
      loadPlanes();
    } catch (err) {
      toast.error('Error');
    }
  };

  const eliminarPlan = async (planId, estudianteNombre) => {
    if (!window.confirm(`¿Eliminar el plan de cuotas de ${estudianteNombre}?\n\nEsto borrará:\n- El plan de cuotas\n- El registro del semestre asociado\n- La factura generada\n\nEl estudiante podrá volver a registrarse con 3 cuotas.`)) return;
    try {
      await axios.delete(`${API}/admin/planes-cuotas/${planId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Plan eliminado. El estudiante puede re-registrarse.');
      loadPlanes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar plan');
    }
  };

  const getEstadoBadge = (estado) => {
    const map = {
      pendiente: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-800' },
      pendiente_aprobacion: { label: 'En Revision', cls: 'bg-blue-100 text-blue-800' },
      pagada: { label: 'Pagada', cls: 'bg-green-100 text-green-800' },
      rechazada: { label: 'Rechazada', cls: 'bg-red-100 text-red-800' }
    };
    const info = map[estado] || { label: estado, cls: 'bg-gray-100' };
    return <Badge className={info.cls}>{info.label}</Badge>;
  };

  const planesFiltrados = planes.filter(p => filtro === 'todos' || p.estado === filtro);

  if (loading) {
    return <div className="flex items-center justify-center min-h-[200px]"><i className="fas fa-spinner fa-spin text-2xl text-blue-500" /></div>;
  }

  const stats = {
    total: planes.length,
    activos: planes.filter(p => p.estado === 'activo').length,
    completados: planes.filter(p => p.estado === 'completado').length,
    pendientes_revision: planes.reduce((acc, p) => acc + p.cuotas?.filter(c => c.estado === 'pendiente_aprobacion').length, 0)
  };

  return (
    <div className="space-y-6" data-testid="admin-planes-cuotas">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <i className="fas fa-file-contract text-blue-600" />
            Planes de Cuotas
          </h2>
          <p className="text-sm text-gray-500 mt-1">Gestiona los planes de pago en 3 cuotas</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: stats.total, icon: 'fa-file-contract', color: 'blue' },
          { label: 'Activos', value: stats.activos, icon: 'fa-clock', color: 'yellow' },
          { label: 'Completados', value: stats.completados, icon: 'fa-check-circle', color: 'green' },
          { label: 'Por Revisar', value: stats.pendientes_revision, icon: 'fa-eye', color: 'red' }
        ].map(s => (
          <div key={s.label} className={`p-3 rounded-xl bg-${s.color}-50 border border-${s.color}-200`}>
            <div className="flex items-center gap-2">
              <i className={`fas ${s.icon} text-${s.color}-500`} />
              <span className="text-xs text-gray-600">{s.label}</span>
            </div>
            <p className={`text-2xl font-bold text-${s.color}-700 mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'todos', label: 'Todos' },
          { key: 'activo', label: 'Activos' },
          { key: 'completado', label: 'Completados' },
          { key: 'cancelado', label: 'Cancelados' }
        ].map(f => (
          <Button
            key={f.key}
            variant={filtro === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFiltro(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Plans list */}
      {planesFiltrados.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          <i className="fas fa-inbox text-3xl mb-3" /><p>No hay planes de cuotas</p>
        </CardContent></Card>
      ) : (
        planesFiltrados.map(plan => (
          <Card key={plan.id} className="overflow-hidden" data-testid={`admin-plan-${plan.id}`}>
            <CardHeader className="pb-3 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <i className="fas fa-user text-blue-500" />
                    {plan.estudiante_nombre}
                    <span className="text-xs text-gray-400 font-normal">{plan.estudiante_email}</span>
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    {plan.semestre_nombre} | Total: <b>{plan.moneda} {plan.monto_total?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}</b>
                    | Firmado: {new Date(plan.fecha_firma).toLocaleDateString('es-UY')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={plan.estado === 'completado' ? 'bg-green-100 text-green-800' : plan.estado === 'activo' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                    {plan.estado}
                  </Badge>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 text-xs"
                    onClick={() => eliminarPlan(plan.id, plan.estudiante_nombre)}
                    data-testid={`delete-plan-${plan.id}`}
                  >
                    <i className="fas fa-trash mr-1" />Eliminar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {plan.cuotas?.map((cuota, idx) => {
                const fechaVenc = new Date(cuota.fecha_vencimiento).toLocaleDateString('es-UY');
                const fechaLim = new Date(cuota.fecha_limite).toLocaleDateString('es-UY');
                const needsReview = cuota.estado === 'pendiente_aprobacion';
                const isPagada = cuota.estado === 'pagada';
                const comprobanteFullUrl = cuota.comprobante_url ? `${BASE_URL}${cuota.comprobante_url}` : null;

                return (
                  <div key={idx} className={`p-4 border-b last:border-b-0 ${needsReview ? 'bg-blue-50 border-l-4 border-l-blue-500' : isPagada ? 'bg-green-50/50' : ''}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          isPagada ? 'bg-green-500' : needsReview ? 'bg-blue-500' : cuota.estado === 'rechazada' ? 'bg-red-500' : 'bg-gray-400'
                        }`}>
                          {isPagada ? <i className="fas fa-check text-xs" /> : needsReview ? <i className="fas fa-clock text-xs" /> : cuota.numero}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Cuota {cuota.numero}: {cuota.moneda} {cuota.monto?.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-gray-500">
                            Vence: {fechaVenc} | Limite: {fechaLim}
                          </p>
                          {isPagada && cuota.fecha_aprobacion && (
                            <p className="text-xs text-green-600 mt-0.5">
                              <i className="fas fa-check-circle mr-1" />
                              Aprobada el {new Date(cuota.fecha_aprobacion).toLocaleDateString('es-UY')}
                            </p>
                          )}
                          {cuota.fecha_pago && !isPagada && (
                            <p className="text-xs text-blue-600 mt-0.5">
                              <i className="fas fa-upload mr-1" />
                              Recibo subido el {new Date(cuota.fecha_pago).toLocaleDateString('es-UY')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {getEstadoBadge(cuota.estado)}
                        {comprobanteFullUrl && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                            onClick={() => setVerRecibo(verRecibo === `${plan.id}-${cuota.numero}` ? null : `${plan.id}-${cuota.numero}`)}
                            data-testid={`ver-recibo-${cuota.numero}`}
                          >
                            <i className="fas fa-eye mr-1" />Ver Recibo
                          </Button>
                        )}
                        {needsReview && (
                          <>
                            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => aprobarCuota(plan.id, cuota.numero)} data-testid={`aprobar-cuota-${cuota.numero}`}>
                              <i className="fas fa-check mr-1" />Aprobar
                            </Button>
                            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => rechazarCuota(plan.id, cuota.numero)} data-testid={`rechazar-cuota-${cuota.numero}`}>
                              <i className="fas fa-times mr-1" />Rechazar
                            </Button>
                          </>
                        )}
                        {cuota.estado === 'pendiente' && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => extenderPlazo(plan.id, cuota.numero)}>
                              <i className="fas fa-calendar-plus mr-1" />Extender
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => marcarPagada(plan.id, cuota.numero)}>
                              <i className="fas fa-hand-holding-usd mr-1" />Pagada
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Receipt preview */}
                    {verRecibo === `${plan.id}-${cuota.numero}` && comprobanteFullUrl && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-600">Comprobante de pago - Cuota {cuota.numero}</p>
                          <a href={comprobanteFullUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                            <i className="fas fa-external-link-alt mr-1" />Abrir en nueva pestaña
                          </a>
                        </div>
                        {comprobanteFullUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img src={comprobanteFullUrl} alt={`Recibo cuota ${cuota.numero}`} className="max-w-full max-h-96 rounded border mx-auto" />
                        ) : (
                          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded">
                            <i className="fas fa-file-pdf text-3xl text-red-500" />
                            <div>
                              <p className="text-sm font-medium">Documento PDF</p>
                              <a href={comprobanteFullUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                Descargar / Ver PDF
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default AdminPlanesCuotas;
